import { useState } from 'react';
import { useSelector } from 'react-redux';

import { datadogRum } from '@datadog/browser-rum';

import { DrawerComponentProps } from 'src/components/drawers/DrawerManager';
import { InputOneTimePasscode } from 'src/components/drawers/PhoneNumberVerificationFlow/InputOneTimePasscode';
import { InputPhoneNumber } from 'src/components/drawers/PhoneNumberVerificationFlow/InputPhoneNumber';
import { VerificationSuccessful } from 'src/components/drawers/PhoneNumberVerificationFlow/VerificationSuccessful';
import { selectCurrentUser, selectFacilityId } from 'src/store/configSlice';
import { useAssignPhoneNumberMutation } from 'src/store/sageAdminApi';

enum VerificationStep {
    Input,
    Otp,
    Success,
}

function VerifyPhoneNumberDrawer(props: DrawerComponentProps) {
    const { onClose, onNext } = props;

    const currentUser = useSelector(selectCurrentUser);
    const facilityId = useSelector(selectFacilityId);

    const [currentStep, setCurrentStep] = useState<VerificationStep>(VerificationStep.Input);
    const [phoneNumber, setPhoneNumber] = useState<string>(currentUser?.phoneNumber || '');

    const [assignPhoneNumber] = useAssignPhoneNumberMutation();

    const staffMemberId = currentUser.id!;

    const goToNextStep = () => {
        if (currentStep < VerificationStep.Success) setCurrentStep(currentStep + 1);
    };

    const goToPreviousStep = () => {
        if (currentStep > VerificationStep.Input) setCurrentStep(currentStep - 1);
    };

    const sendVerificationCode = async (number: string) => {
        try {
            await assignPhoneNumber({
                staffMemberId,
                facilityRid: facilityId,
                body: { phoneNumber: number },
            }).unwrap();
        } catch (error: any) {
            datadogRum.addError(error, {
                message: 'Error resending verification code',
                staffMemberId,
            });
        }
    };

    const handleInputNext = async (phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
        await sendVerificationCode(phoneNumber);
        goToNextStep();
    };

    return (
        <>
            {currentStep === VerificationStep.Input && (
                <InputPhoneNumber
                    defaultPhoneNumber={phoneNumber}
                    onNext={handleInputNext}
                    onCancel={onClose}
                />
            )}
            {currentStep === VerificationStep.Otp && (
                <InputOneTimePasscode
                    phoneNumber={phoneNumber}
                    staffMemberId={staffMemberId}
                    onSendCode={() => sendVerificationCode(phoneNumber)}
                    onBack={goToPreviousStep}
                    onSuccess={goToNextStep}
                />
            )}
            {currentStep === VerificationStep.Success && (
                <VerificationSuccessful onClose={onClose} onContinue={() => onNext()} />
            )}
        </>
    );
}

export default VerifyPhoneNumberDrawer;
